import { Input, Label } from "@drs-ops/ui";
import { useDebounce } from "@drs-ops/ui/hooks";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTripPlannerApi } from "@/hooks/useTripPlannerApi";
import type { AutocompleteSuggestion } from "@/types/tripPlanner";

/**
 * Shortest input we are willing to send to Places (New) Autocomplete. Every call is billed,
 * and one or two characters can't produce a useful venue prediction. Keep in sync with the
 * Worker-side floor in `worker/handlers/googleAutocomplete.ts` (the Worker is the real gate;
 * `src` and `worker` are separate tsconfig projects so the constant can't be shared).
 */
export const MIN_AUTOCOMPLETE_CHARS = 3;

/** Debounce before a keystroke becomes a billed request. */
const DEBOUNCE_MS = 300;

/** Predictions for a given input barely drift; serve repeats from the client cache. */
const SUGGESTION_STALE_MS = 5 * 60 * 1000;

function newSessionToken(): string {
  return crypto.randomUUID();
}

export interface VenueAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  id?: string;
}

export function VenueAutocomplete({
  value,
  onChange,
  label = "Venue address",
  id = "venue-address",
}: VenueAutocompleteProps): React.ReactElement {
  const [draft, setDraft] = useState(value);
  const [showList, setShowList] = useState(false);
  // One Places session token per picking session, rotated once a suggestion is accepted so the
  // next search starts a fresh session instead of extending a finished one.
  const [sessionToken, setSessionToken] = useState(newSessionToken);
  // The text we just accepted from the list. Selecting a suggestion pushes it into `draft`,
  // which would otherwise debounce straight into another billed lookup of itself.
  const [accepted, setAccepted] = useState<string | null>(null);
  const debounced = useDebounce(draft, DEBOUNCE_MS);
  const { autocomplete } = useTripPlannerApi();

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const trimmed = debounced.trim();
  // React Query keys the request on (token, input), so a re-render can never re-fire it —
  // only a new input can. `retry: false` because a failed autocomplete is still billed.
  const query = useQuery({
    queryKey: ["places-autocomplete", sessionToken, trimmed],
    queryFn: () => autocomplete(trimmed, sessionToken),
    enabled: trimmed.length >= MIN_AUTOCOMPLETE_CHARS && trimmed !== accepted,
    retry: false,
    staleTime: SUGGESTION_STALE_MS,
    gcTime: SUGGESTION_STALE_MS,
  });

  const suggestions: AutocompleteSuggestion[] = query.data?.suggestions ?? [];

  const handleSelect = (s: AutocompleteSuggestion) => {
    setDraft(s.description);
    setAccepted(s.description.trim());
    setSessionToken(newSessionToken());
    onChange(s.description);
    setShowList(false);
  };

  return (
    <div className="relative">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setAccepted(null);
          setShowList(true);
          onChange(e.target.value);
        }}
        onFocus={() => suggestions.length > 0 && setShowList(true)}
        onBlur={() => setTimeout(() => setShowList(false), 150)}
        autoComplete="off"
      />
      {showList && suggestions.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full rounded-md border border-border bg-popover text-sm shadow-md">
          {suggestions.map((s) => (
            <li key={s.place_id}>
              <button
                type="button"
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent"
                onClick={() => handleSelect(s)}
              >
                <span className="font-medium">{s.main_text}</span>
                <span className="text-xs text-muted-foreground">{s.secondary_text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
